import { Tag } from 'antd'
import {
  calculateRequiredCoverage,
  getCofModifier,
  getEffectiveStrategyType,
  getEffectiveZoneArea,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import { FC } from 'react'
import styled from 'styled-components'

const CompactSummary = styled.div`
  margin-bottom: 12px;
  padding: 12px 16px;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`

const SummaryLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`

const SummaryRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StatBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 12px;
  background: ${({ theme }) => theme.forecast.background3};
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
`

const StatLabel = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.forecast.defaultText};
`

export interface ZoneSummaryCardProps {
  zone: ZoneParams
}

export const ZoneSummaryCard: FC<ZoneSummaryCardProps> = ({ zone }) => {
  const { cof, confidence } = useZoneAnalysisData()
  const strategyType = getEffectiveStrategyType(zone.nii_strategy)
  const zoneArea = getEffectiveZoneArea(zone)
  const coverage = calculateRequiredCoverage(
    zone,
    getCofModifier(cof.level),
    confidence
  )

  return (
    <CompactSummary>
      <SummaryLeft>
        <StatBadge>
          <StatLabel>Area</StatLabel>
          <StatValue>{zoneArea.toFixed(2)} m²</StatValue>
        </StatBadge>

        {strategyType && (
          <StatBadge>
            <StatLabel>Strategy</StatLabel>
            <StatValue>Type {strategyType}</StatValue>
          </StatBadge>
        )}

        {coverage?.required_coverage_percent !== undefined && (
          <StatBadge>
            <StatLabel>Required</StatLabel>
            <StatValue>
              {coverage.required_coverage_percent.toFixed(1)}%
            </StatValue>
          </StatBadge>
        )}

        {coverage?.required_coverage_area_sq_m !== undefined && (
          <StatBadge>
            <StatLabel>To Inspect</StatLabel>
            <StatValue>
              {coverage.required_coverage_area_sq_m.toFixed(2)} m²
            </StatValue>
          </StatBadge>
        )}
      </SummaryLeft>

      <SummaryRight>
        {zone.damage_mechanisms && zone.damage_mechanisms.length > 0 && (
          <Tag color='orange'>{zone.damage_mechanisms.length} DMs</Tag>
        )}
      </SummaryRight>
    </CompactSummary>
  )
}
